import { ExportOutlined, MenuFoldOutlined, MenuUnfoldOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { ChannelDoc, StoreSearchIndex } from '@gooduncles/gu-app-schema';
import { Button, Popover, Segmented, Select, Tag, Tooltip } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { markChannelInboxRead } from 'src/utils/channel/channel-inbox-util';
import { getChannel, getStoreSearchIndexes } from 'src/utils/firebase/firebase-short-cut';

import useChannelInbox from 'src/hooks/useChannelInbox';

import styles from './ChatRoom.module.scss';

import ChatRoomDetail from './ChatRoomDetail';
import StoreInfoPanel from './StoreInfoPanel/StoreInfoPanel';
import StorePushStatus from './StorePushStatus/StorePushStatus';

type StoreSearchItem = StoreSearchIndex & { _id: string };

type InboxTab = 'unread' | 'all';

const CHANNEL_PREFIX = 'store_';

/** 각 탭에 어떤 목록이 노출되는지 설명하는 도움말 */
const TAB_HELP: Record<InboxTab, { title: string; description: string }> = {
  unread: {
    title: '안읽음',
    description: '관리자(콘솔)가 아직 열어보지 않은 채팅입니다. 채널을 열면 자동으로 목록에서 사라집니다.',
  },
  all: {
    title: '최근',
    description: '메시지가 한 번 이상 오간 모든 채팅을 최근 활동순으로 보여줍니다. (최대 50개)',
  },
};

/** 탭 라벨 + 목록 설명 팝업 */
const TabLabel: FC<{ tab: InboxTab; text: string }> = ({ tab, text }) => {
  const help = TAB_HELP[tab];
  return (
    <span className={styles.tabLabel}>
      {text}
      <Popover title={help.title} content={<div className={styles.tabHelpContent}>{help.description}</div>}>
        <QuestionCircleOutlined className={styles.tabHelpIcon} onClick={(e) => e.stopPropagation()} />
      </Popover>
    </span>
  );
};

/** channelId에서 storeId 추출 */
const extractStoreId = (channelId: string | undefined): string | null => {
  if (!channelId?.startsWith(CHANNEL_PREFIX)) return null;
  return channelId.slice(CHANNEL_PREFIX.length);
};

/** ISO 8601 시각을 상대적 텍스트로 변환 */
const formatRelativeTime = (isoString: string): string => {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '방금 전';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return `${days}일 전`;
};

interface InboxRow {
  channelId: string;
  storeId: string | null;
  name: string;
  code: string | null;
  preview: string;
  timeText: string;
  unread: boolean;
  /** 마지막 메시지를 고객(commerce)이 보냄 → 답변 대기 */
  awaitingReply: boolean;
}

/** 채널 문서를 사이드바 행 표시 모델로 변환 */
const toInboxRow = (channel: ChannelDoc, storeMap: Map<string, StoreSearchItem>): InboxRow => {
  const storeId = extractStoreId(channel._id);
  const store = storeId ? storeMap.get(storeId) : undefined;
  const inbox = channel.consoleInbox;
  return {
    channelId: channel._id,
    storeId,
    name: store?.storeNickname ?? channel.name,
    code: store?.storeCode ?? null,
    preview: inbox?.lastMessagePreview ?? '',
    timeText: inbox ? formatRelativeTime(inbox.lastMessageAt) : '',
    unread: inbox?.adminUnread ?? false,
    awaitingReply: inbox?.lastMessageSenderService === 'commerce',
  };
};

const ChatRoom: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const channelId = searchParams.get('channelId') ?? undefined;

  const [stores, setStores] = useState<StoreSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tab, setTab] = useState<InboxTab>('unread');
  // 현재 열린 채널이 받은편지함 목록 밖일 때(검색 점프·limit 초과) 맨 위에 고정 노출하기 위한 단건 조회 결과
  const [pinnedChannel, setPinnedChannel] = useState<ChannelDoc | null>(null);

  const { recentChannels, unreadChannels, unreadCount } = useChannelInbox();

  const selectedStoreId = extractStoreId(channelId);

  useEffect(() => {
    setLoading(true);
    getStoreSearchIndexes()
      .then((data) => setStores(data))
      .finally(() => setLoading(false));
  }, []);

  // 채널을 열면 단건 조회 — 목록 밖 채널 고정용 + 안읽음이면 받은편지함 읽음 처리
  useEffect(() => {
    if (!channelId) {
      setPinnedChannel(null);
      return;
    }
    let cancelled = false;
    getChannel(channelId).then((channel) => {
      if (cancelled) return;
      setPinnedChannel(channel ?? null);
      if (channel?.consoleInbox?.adminUnread) {
        markChannelInboxRead(channelId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [channelId]);

  const storeMap = useMemo(() => {
    const map = new Map<string, StoreSearchItem>();
    stores.forEach((store) => map.set(store._id, store));
    return map;
  }, [stores]);

  const storeOptions = useMemo(
    () =>
      stores.map((store) => ({
        value: store._id,
        label: `${store.storeNickname} (${store.storeCode})`,
      })),
    [stores],
  );

  // 탭별 목록 + 현재 열린 채널이 목록 밖이면 맨 위에 고정
  const displayChannels = useMemo(() => {
    const list = tab === 'unread' ? unreadChannels : recentChannels;
    if (!channelId || list.some((channel) => channel._id === channelId)) return list;
    const active =
      recentChannels.find((channel) => channel._id === channelId) ??
      unreadChannels.find((channel) => channel._id === channelId) ??
      pinnedChannel;
    return active?._id === channelId ? [active, ...list] : list;
  }, [tab, unreadChannels, recentChannels, channelId, pinnedChannel]);

  const handleSelect = (storeId: string) => {
    setSearchParams({ channelId: `${CHANNEL_PREFIX}${storeId}` });
  };

  const handleClear = () => {
    setSearchParams({});
  };

  const handleChannelClick = (clickedChannelId: string) => {
    setSearchParams({ channelId: clickedChannelId });
  };

  const handleOpenInNewWindow = () => {
    if (!channelId) return;
    const url = `${window.location.origin}/chat-popup?channelId=${encodeURIComponent(channelId)}`;
    window.open(url, `chat-popup-${channelId}`, 'width=480,height=720,menubar=no,toolbar=no,location=no,status=no');
  };

  return (
    <div className={styles.container}>
      {sidebarOpen && (
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarTitleRow}>
              <h3>매장 채팅</h3>
              <Tooltip title='목록 닫기'>
                <Button
                  type='text'
                  size='small'
                  icon={<MenuFoldOutlined />}
                  onClick={() => setSidebarOpen(false)}
                  aria-label='매장 채팅 목록 닫기'
                />
              </Tooltip>
            </div>
            <Select
              showSearch
              placeholder='매장 검색'
              optionFilterProp='label'
              loading={loading}
              options={storeOptions}
              onChange={handleSelect}
              value={selectedStoreId}
              allowClear
              onClear={handleClear}
              style={{ width: '100%' }}
            />
            <Segmented
              block
              className={styles.tabBar}
              value={tab}
              onChange={(value) => setTab(value as InboxTab)}
              options={[
                {
                  label: <TabLabel tab='unread' text={unreadCount > 0 ? `안읽음 ${unreadCount}` : '안읽음'} />,
                  value: 'unread',
                },
                { label: <TabLabel tab='all' text='최근' />, value: 'all' },
              ]}
            />
          </div>
          <div className={styles.channelList}>
            {displayChannels.length === 0 ? (
              <div className={styles.emptyList}>
                {tab === 'unread' ? '안읽은 채팅이 없습니다.' : '채팅 내역이 없습니다.'}
              </div>
            ) : (
              displayChannels.map((channel) => {
                const row = toInboxRow(channel, storeMap);
                const isActive = channel._id === channelId;
                return (
                  <div
                    key={row.channelId}
                    className={`${styles.channelItem} ${isActive ? styles.channelItemActive : ''}`}
                    onClick={() => handleChannelClick(row.channelId)}
                  >
                    <div className={styles.channelItemTopRow}>
                      <div className={styles.channelNameWrap}>
                        {row.unread && <span className={styles.unreadDot} />}
                        <span className={styles.channelName}>{row.name}</span>
                        {row.code && <span className={styles.channelCode}>{row.code}</span>}
                      </div>
                      <div className={styles.channelMeta}>
                        {row.timeText && <span className={styles.channelTime}>{row.timeText}</span>}
                        {row.storeId && <StorePushStatus storeId={row.storeId} />}
                      </div>
                    </div>
                    <div className={styles.channelItemBottomRow}>
                      <span className={styles.channelPreview}>{row.preview || '메시지 없음'}</span>
                      {row.awaitingReply && (
                        <Tag color='orange' className={styles.replyTag}>
                          답변 대기
                        </Tag>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <div className={`${styles.chatArea} ${!sidebarOpen ? styles.chatAreaSidebarClosed : ''}`}>
        {!sidebarOpen && (
          <div className={styles.chatToolbar}>
            <Tooltip title='목록 열기'>
              <Button
                type='text'
                size='small'
                icon={<MenuUnfoldOutlined />}
                onClick={() => setSidebarOpen(true)}
                aria-label='매장 채팅 목록 열기'
              />
            </Tooltip>
          </div>
        )}
        {channelId ? (
          <div className={styles.chatBody}>
            <ChatRoomDetail
              channelId={channelId}
              headerExtra={
                <Tooltip title='새 창에서 열기'>
                  <Button
                    type='text'
                    size='small'
                    icon={<ExportOutlined />}
                    onClick={handleOpenInNewWindow}
                    aria-label='새 창에서 열기'
                  />
                </Tooltip>
              }
            />
          </div>
        ) : (
          <div className={styles.emptyState}>매장을 선택하면 채팅이 표시됩니다.</div>
        )}
      </div>
      <StoreInfoPanel storeId={selectedStoreId} />
    </div>
  );
};

export default ChatRoom;
